import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { EmptyState } from '@synoptic/ui-kit/empty-state/empty-state.tsx';
import { PackagesIcon } from '@synoptic/ui-kit/icons/react/packages.tsx';
import {
  ModalContent,
  ModalHeader,
  ModalRoot,
  ModalTitle,
  ModalTrigger,
} from '@synoptic/ui-kit/modal/modal.tsx';
import { useControllableState } from '@synoptic/ui-kit/utils/use-controllable-state.js';
import { FC, ReactNode, useState } from 'react';
import {
  PackageInStreamFragmentFragment,
  StripePriceFragmentFragment,
} from '@/__generated__/graphql.ts';
import { PaymentOption } from '@/components/streams/payment-options/payment-options-enum';
import { PaymentOptionsContent } from '@/components/streams/payment-options/payment-options.tsx';
import {
  buttonsContainer,
  emptyPackages,
  modalContent,
  packageName,
  packagesList,
} from '@/components/streams/toggle-paid-stream-subscription/stream-included-in-package-modal.css.ts';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { convertCentsToDollars } from '@/utils/convert-cents-to-dollars.ts';
import { getMonthlyPrice } from '@/utils/paid-data.ts';

export const ChoosePackageModal: FC<{
  trigger: ReactNode;
  packages: PackageInStreamFragmentFragment[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  streamId?: string;
  prices?: StripePriceFragmentFragment[];
  title: string;
  actionTitle: string;
  onStreamUnlockOpen?: () => void;
  onStreamUnlockSubmit?: (
    e: React.FormEvent<HTMLFormElement>,
    values: { paymentOption: PaymentOption },
  ) => void;
  onSubmit?: () => void;
}> = ({
  trigger,
  packages,
  open,
  onOpenChange,
  streamId,
  prices,
  title,
  actionTitle,
  onStreamUnlockOpen,
  onStreamUnlockSubmit,
  onSubmit,
}) => {
  const [isOpen, setIsOpen] = useControllableState({
    value: open,
    defaultValue: false,
    onChange: onOpenChange,
  });
  const [unlockPackage, setUnlockPackage] = useState(false);

  if (!isOpen && unlockPackage) {
    setUnlockPackage(false);
  }

  return (
    <ModalRoot open={isOpen} onOpenChange={setIsOpen}>
      <ModalTrigger asChild>{trigger}</ModalTrigger>
      {packages.length === 0 ? (
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{title}</ModalTitle>
          </ModalHeader>

          <EmptyState
            className={emptyPackages}
            illustration={<PackagesIcon />}
            heading="This stream isn't part of any package yet"
            description="Please check back later to see if it's available for purchase."
          />
        </ModalContent>
      ) : !unlockPackage ? (
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{title}</ModalTitle>
          </ModalHeader>

          <div className={modalContent}>
            <p>This stream is part of packages that offer even more value:</p>

            <div className={packagesList}>
              {packages.map((pack) => (
                <div key={pack.id}>
                  -{' '}
                  <Link
                    to={paths.makeStreamPackage(pack.id)}
                    className={packageName}
                  >
                    {pack.name}
                  </Link>{' '}
                  -{' '}
                  <span>
                    $
                    {convertCentsToDollars(
                      getMonthlyPrice(pack.packagePaidData) ?? 0,
                    )}
                    /month
                  </span>
                </div>
              ))}
            </div>

            <p>Explore the packages and make the best deal!</p>

            <div className={buttonsContainer}>
              <Button
                size={'large'}
                onClick={() => {
                  if (streamId) {
                    setUnlockPackage(true);
                    onStreamUnlockOpen?.();
                  } else {
                    onSubmit?.();
                    setIsOpen(false);
                  }
                }}
              >
                {actionTitle}
              </Button>
            </div>
          </div>
        </ModalContent>
      ) : streamId ? (
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Unlock stream</ModalTitle>
          </ModalHeader>

          <PaymentOptionsContent
            action={paths.makeStreamUnlock(streamId)}
            onSubmit={onStreamUnlockSubmit}
            prices={prices}
          />
        </ModalContent>
      ) : null}
    </ModalRoot>
  );
};
